/**
 * useMealPlans Hook
 *
 * Custom React hook for managing meal plans data with filtering, pagination,
 * loading states, and error handling. Integrates with the MealDomain service
 * to fetch and manage meal plan information.
 *
 * @module hooks/useMealPlans
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { apiService } from '@/services/ApiService';
import { MealApiError } from '@/services/domains/MealDomain';
import type { MealPlanFrontend, GetMealPlansParams } from '@/types/mealPlan';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Meal plans filter state interface
 */
export interface MealPlansFilterState {
  /** Current page number (1-based) */
  page: number;
  /** Number of items per page */
  pageSize: number;
}

/**
 * Pagination information interface
 */
export interface MealPlansPagination {
  /** Current page number */
  page: number;
  /** Number of items per page */
  perPage: number;
  /** Total number of meal plans */
  total: number;
  /** Total number of pages */
  totalPages: number;
}

/**
 * Return type for useMealPlans hook
 */
export interface UseMealPlansReturn {
  /** Array of meal plans */
  mealPlans: MealPlanFrontend[];
  /** Pagination information */
  pagination: MealPlansPagination;
  /** Loading state indicator */
  loading: boolean;
  /** Error message if request failed */
  error: string | null;
  /** Function to refresh meal plans data */
  refresh: () => Promise<void>;
  /** Function to manually trigger fetch with new parameters */
  fetchMealPlans: (params?: GetMealPlansParams) => Promise<void>;
  /** Function to delete a meal plan */
  deleteMealPlan: (mealPlanId: string, mealPlanLabel: string) => Promise<void>;
}

/**
 * Hook for managing meal plans with filtering and pagination
 *
 * @param filterState - Current filter and pagination state
 * @returns Object containing meal plans data, pagination info, loading state, error state, and refresh function
 *
 * @example
 * ```typescript
 * const MealPlansComponent = () => {
 *   const [filterState, setFilterState] = useState({
 *     searchTerm: '',
 *     statusFilter: 'all',
 *     page: 1,
 *     pageSize: 10
 *   });
 *
 *   const { mealPlans, pagination, loading, error, refresh } = useMealPlans(filterState);
 *
 *   if (loading) return <div>Loading meal plans...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *
 *   return (
 *     <div>
 *       {mealPlans.map(mealPlan => (
 *         <div key={mealPlan.id}>{mealPlan.label}</div>
 *       ))}
 *       <div>Page {pagination.page} of {pagination.totalPages}</div>
 *     </div>
 *   );
 * };
 * ```
 */
export const useMealPlans = (filterState: MealPlansFilterState): UseMealPlansReturn => {
  const logger = getLogger();
  const navigate = useNavigate();

  // State management
  const [mealPlans, setMealPlans] = useState<MealPlanFrontend[]>([]);
  const [pagination, setPagination] = useState<MealPlansPagination>({
    page: 1,
    perPage: 10,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch meal plans from the API
   */
  const fetchMealPlans = useCallback(async (params?: GetMealPlansParams) => {
    try {
      setLoading(true);
      setError(null);

      logger.log('Fetching meal plans with params:', params || { page: filterState.page, per_page: filterState.pageSize });

      const response = await apiService.mealplan.getMealPlans({
        page: params?.page || filterState.page,
        per_page: params?.per_page || filterState.pageSize,
      });

      logger.log('Meal plans fetched successfully:', {
        count: response.mealPlans.length,
        total: response.total,
        page: response.page,
        totalPages: response.totalPages,
      });

      // Extract unique creator IDs and fetch their photo URLs
      const creatorIds = [...new Set(response.mealPlans.map(plan => plan.creatorId))];
      let photoUrls: Record<string, string | null> = {};

      if (creatorIds.length > 0) {
        try {
          const photoResponse = await apiService.photo.getPhotoUrls({ user_ids: creatorIds });
          photoUrls = photoResponse.photo_urls;
          logger.log('Creator photo URLs fetched successfully:', photoUrls);
        } catch (photoError) {
          logger.warn('Failed to fetch creator photo URLs:', photoError);
          // Continue without photos - don't fail the entire request
        }
      }

      // Add photo URLs to meal plans
      const mealPlansWithPhotos = response.mealPlans.map(mealPlan => ({
        ...mealPlan,
        creatorPhotoUrl: photoUrls[mealPlan.creatorId] || null
      }));

      // Update state with API response
      setMealPlans(mealPlansWithPhotos);
      setPagination({
        page: response.page,
        perPage: response.perPage,
        total: response.total,
        totalPages: response.totalPages,
      });

    } catch (err) {
      logger.error('Failed to fetch meal plans:', err);

      let errorMessage = 'Failed to load meal plans. Please try again.';

      if (err instanceof MealApiError) {
        switch (err.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in to continue.';
            break;
          case 403:
            errorMessage = 'You do not have permission to view meal plans.';
            // Redirect to dashboard on 403 error
            navigate('/dashboard', { replace: true });
            return; // Exit early to prevent setting error state
          case 404:
            errorMessage = 'Meal plans not found.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || errorMessage;
        }
      }

      setError(errorMessage);
      setMealPlans([]);
      setPagination({
        page: 1,
        perPage: filterState.pageSize,
        total: 0,
        totalPages: 0,
      });
    } finally {
      setLoading(false);
    }
  }, [filterState.page, filterState.pageSize, navigate]);

  /**
   * Refresh meal plans data
   */
  const refresh = useCallback(async () => {
    await fetchMealPlans();
  }, [fetchMealPlans]);

  /**
   * Delete a meal plan
   */
  const deleteMealPlan = useCallback(async (mealPlanId: string, mealPlanLabel: string) => {
    try {
      logger.log('Deleting meal plan:', { mealPlanId, mealPlanLabel });

      await apiService.mealplan.deleteMealPlan(mealPlanId);

      logger.log('Meal plan deleted successfully:', mealPlanId);

      // Refresh the meal plans list after successful deletion
      await refresh();
    } catch (err) {
      logger.error('Failed to delete meal plan:', err);

      let errorMessage = `Failed to delete "${mealPlanLabel}". Please try again.`;

      if (err instanceof MealApiError) {
        switch (err.status) {
          case 401:
            errorMessage = 'Authentication required. Please log in to continue.';
            break;
          case 403:
            errorMessage = `You do not have permission to delete "${mealPlanLabel}".`;
            // Redirect to dashboard on 403 error
            navigate('/dashboard', { replace: true });
            return; // Exit early to prevent showing alert
          case 404:
            errorMessage = `Meal plan "${mealPlanLabel}" not found.`;
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = err.message || errorMessage;
        }
      }

      // Show error to user
      alert(errorMessage);
      throw err; // Re-throw so calling component can handle if needed
    }
  }, [refresh, navigate]);

  /**
   * Automatically fetch meal plans when filter state changes
   */
  useEffect(() => {
    fetchMealPlans();
  }, [filterState.page, filterState.pageSize]);

  return {
    mealPlans,
    pagination,
    loading,
    error,
    refresh,
    fetchMealPlans,
    deleteMealPlan,
  };
};

export default useMealPlans;